import { QuoteRequestLine } from "../types";

export const MAX_BATCH_SIZE = 6;

export type RequiredFieldForGrouping = Pick<
  QuoteRequestLine,
  "id" | "assemblyParentId" | "batchId"
>;

export type AssemblyWithChildren<T extends RequiredFieldForGrouping> = {
  quoteRequestLine: T;
  children: AssemblyWithChildren<T>[];
};

const groupPartsByHierarchy = <T extends RequiredFieldForGrouping>(
  quoteRequestLines: T[],
): AssemblyWithChildren<T>[] => {
  const quoteRequestLinesMap = new Map<string, AssemblyWithChildren<T>>();

  quoteRequestLines.forEach((quoteRequestLine) => {
    quoteRequestLinesMap.set(quoteRequestLine.id, {
      quoteRequestLine,
      children: [],
    });
  });

  const rootElements: AssemblyWithChildren<T>[] = [];

  quoteRequestLines.forEach((quoteRequestLine) => {
    const currentElement = quoteRequestLinesMap.get(quoteRequestLine.id)!;

    const assemblyParentId = quoteRequestLine.assemblyParentId;

    if (assemblyParentId === null) {
      rootElements.push(currentElement);
    } else {
      const parentElement = quoteRequestLinesMap.get(assemblyParentId);
      if (parentElement) {
        parentElement.children.push(currentElement);
      } else {
        rootElements.push(currentElement);
      }
    }
  });

  return rootElements;
};

const groupAssembliesByBatch = <T extends RequiredFieldForGrouping>(
  roots: AssemblyWithChildren<T>[],
): Map<string, AssemblyWithChildren<T>[]> => {
  const batches = new Map<string, AssemblyWithChildren<T>[]>();

  for (const root of roots) {
    const batchId = root.quoteRequestLine.batchId;
    const quoteRequestLineId = root.quoteRequestLine.id;
    const key = batchId ?? quoteRequestLineId;

    const arr = batches.get(key);
    if (arr) {
      arr.push(root);
    } else {
      batches.set(key, [root]);
    }
  }

  return batches;
};

export const groupQuoteRequestLinesByBatch = <T extends RequiredFieldForGrouping>(
  quoteRequestLines: T[],
): Map<string, AssemblyWithChildren<T>[]> => {
  const assemblies = groupPartsByHierarchy(quoteRequestLines);
  const batches = groupAssembliesByBatch(assemblies);

  return batches;
};
